import { Button, Input, InputWrapper } from "@components";
import { COUNTRIES } from "@utilities/constants";
import { toIsoDate } from "@utilities/functions";
import { Client } from "@utilities/types";
import React, { useCallback, useMemo, useState } from "react";

interface Props {
    client?: Client;
    onCreateClient: (client: Partial<Client>) => void;
    onCancel: () => void;
}

export const AddClientForm: React.FC<Props> = ({
    client,
    onCreateClient,
    onCancel
}) => {
    
    const [firstname, setFirstname] = useState<string>(client?.firstname || "");
    const [lastname, setLastname] = useState<string>(client?.lastname || "");
    const [phoneNumber, setPhoneNumber] = useState<string>(client?.phoneNumber || "");
    const [email, setEmail] = useState<string>(client?.email || "");
    const [birth, setBirth] = useState<string>(client?.birth ? toIsoDate(client.birth) : "");
    const [city, setCity] = useState<string>(client?.city || "");
    const [country, setCountry] = useState<string>(client?.country || "");

    const countriesOptions = useMemo(()=> (
        COUNTRIES.map((country)=> ({
            label: country.name,
            value: country.code,
        }))
    ), []);

    const citiesOptions = useMemo(()=> {
        if(!country) return [];
        const countryTmp = COUNTRIES.find((ctry)=> ctry.code === country);
        if(!countryTmp) return [];
        return countryTmp.cities.map((city)=> ({
            label: city,
            value: city,
        }));
    }, [country]);

    const isValidForm = useMemo(()=> (
        firstname.length > 0 && lastname.length > 0 &&
        phoneNumber.length > 0 && email.length > 0 &&
        birth.length > 0 && city.length > 0 &&
        country.length > 0
    ), [birth, city, country, email, firstname, lastname, phoneNumber]);

    const handleCancel = useCallback(()=> {
        setFirstname("");
        setLastname("");
        setPhoneNumber("");
        setEmail("");
        setBirth("");
        setCity("");
        setCountry("");
        onCancel();
    }, [onCancel]);

    const handleSubmit = useCallback(() => {
        if(!isValidForm) return;

        onCreateClient({
            firstname, lastname,
            phoneNumber, email,
            city, country,
            birth: new Date(birth),
        });
        handleCancel();
    }, [
        birth, city, country, email,  phoneNumber,
        firstname, isValidForm, lastname, 
        handleCancel, onCreateClient
    ]);

    return (
        <div className="space-y-4 mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InputWrapper label="prénom">
                    <Input
                        placeholder="Prénom"
                        className="input input-bordered w-full"
                        value={firstname}
                        onChange={(e) => setFirstname(e.target.value)}
                    />
                </InputWrapper>
                <InputWrapper label="nom">
                    <Input
                        placeholder="Nom"
                        className="input input-bordered w-full"
                        value={lastname}
                        onChange={(e) => setLastname(e.target.value)}
                    />
                </InputWrapper>
                <InputWrapper label="Téléphone">
                    <Input
                        type='number'
                        placeholder="Téléphone"
                        className="input input-bordered w-full"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                    />
                </InputWrapper>
                <InputWrapper label="Email">
                    <Input
                        placeholder="Email"
                        className="input input-bordered w-full"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                </InputWrapper>
                <InputWrapper label="Date de Naissance">
                    <Input
                        type='date'
                        placeholder="Date de Naissance"
                        className="input input-bordered w-full"
                        value={birth}
                        onChange={(e) => setBirth(e.target.value)}
                    />
                </InputWrapper>
                <InputWrapper label="Pays">
                    <Input
                        type="select"
                        placeholder="Selectionner un pays"
                        options={countriesOptions}
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                    />
                </InputWrapper>

                <InputWrapper label="Ville">
                    <Input
                        type="select"
                        placeholder="Selectionner une ville"
                        disabled={!country}
                        options={citiesOptions}
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                    />
                </InputWrapper>
            </div>

            {/* Boutons d'action */}
            <div className="flex flex-col sm:flex-row sm:space-x-4 w-full">
                <Button
                    onClick={handleCancel}
                    className="btn btn-secondary w-full sm:w-1/2 text-sm sm:text-base mb-2 sm:mb-0"
                >
                    Annuler
                </Button>
                <Button
                    onClick={handleSubmit}
                    className="btn btn-primary w-full sm:w-1/2 text-sm sm:text-base"
                >
                    {client ? 'Mettre à jour' : 'Ajouter le client'}
                </Button>
            </div>
        </div>
    );
};

